import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { LOCALE_CONFIG } from '@locale/locale-config.provider';
import { MaskitoPipe } from '@maskito/angular';
import { maskitoGetCountryFromNumber, maskitoPhoneOptionsGenerator } from '@maskito/phone';
import { PatientContact } from '@pixacare/domain';
import { PatientContactHttpService } from '@services/http/patient-contact.http.service';
import { TuiButton, TuiDataList, TuiDropdownContent, TuiLoader, TuiTextfieldComponent } from '@taiga-ui/core';
import { TuiCountryIsoCode } from '@taiga-ui/i18n';
import {
  TuiChevron,
  TuiFlagPipe,
  TuiInputPhoneInternational,
  tuiInputPhoneInternationalOptionsProvider,
  TuiSelect,
  TuiSortCountriesPipe,
} from '@taiga-ui/kit';
import { getCountries } from 'libphonenumber-js';
import metadata from 'libphonenumber-js/max/metadata';
import {
  BehaviorSubject,
  combineLatest,
  defer,
  distinctUntilChanged,
  EMPTY,
  first,
  map,
  Observable,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs';

type OnChangeFn = (value: PatientContact) => void;

@Component({
  selector: 'pxc-patient-contact-input',
  imports: [
    CommonModule,
    TuiSelect,
    TuiChevron,
    TuiDropdownContent,
    TuiDataList,
    FormsModule,
    ReactiveFormsModule,
    TuiButton,
    TuiLoader,
    TuiSortCountriesPipe,
    TuiFlagPipe,
    MaskitoPipe,
    TranslocoDirective,
    TuiTextfieldComponent,
    TuiInputPhoneInternational,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: PatientContactInputComponent,
    },
    tuiInputPhoneInternationalOptionsProvider({
      metadata: defer(async () => import('libphonenumber-js/max/metadata').then((m) => m.default)),
      separator: ' ',
    }),
  ],
  templateUrl: './patient-contact-input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PatientContactInputComponent implements ControlValueAccessor, OnChanges {
  private readonly patientContactService = inject(PatientContactHttpService);
  private readonly translocoService = inject(TranslocoService);
  private readonly localeConfig = inject(LOCALE_CONFIG);

  @Input() patientId: number;
  @Input() clientCode: string;
  @Input() inputPlaceholder = this.translocoService.translate('Saisir un numéro de téléphone');
  @Input() selectPlaceholder = this.translocoService.translate('Sélectionner un numéro de téléphone');
  @Input() countryIsoCode: TuiCountryIsoCode = this.localeConfig.tuiConfig.countryIsoCode;

  private readonly _value$ = new BehaviorSubject<PatientContact | null>(null);
  readonly value$ = this._value$.pipe(distinctUntilChanged());

  touched = false;
  disabled = false;

  items$: Observable<PatientContact[]>;
  readonly search$ = new BehaviorSubject<string>('');

  readonly _countryIsoCode$ = new BehaviorSubject<TuiCountryIsoCode>(this.countryIsoCode);
  readonly countryIsoCode$ = this._countryIsoCode$.pipe(distinctUntilChanged());

  readonly _isInputMode$ = new BehaviorSubject<boolean>(false);
  readonly isInputMode$ = this._isInputMode$.pipe(distinctUntilChanged());

  input = '';

  readonly mask = maskitoPhoneOptionsGenerator({
    metadata,
    separator: ' ',
  });

  protected readonly countries = getCountries();

  onChange: OnChangeFn = () => {};
  onTouched: () => void = () => {};

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.patientId || !changes.clientCode) {
      return;
    }

    this.items$ = this.patientContactService.getPatientContacts(this.patientId, this.clientCode).pipe(shareReplay(1));

    combineLatest([this.value$, this.items$])
      .pipe(
        first(),
        switchMap(([value]) => {
          if (!value) {
            return this.selectFirstItem().pipe(
              tap((item) => {
                const hasValue = !!item;
                this._isInputMode$.next(!hasValue);
              }),
            );
          }
          return EMPTY;
        }),
      )
      .subscribe();
  }

  selectFirstItem(): Observable<PatientContact | null> {
    return this.items$.pipe(
      first(),
      map((items) => items?.[0] ?? null),
      tap((item) => {
        if (item) {
          this.onValueChange(item);
        }
      }),
    );
  }

  onValueChange(value: PatientContact | null): void {
    if (value === null) {
      // happen when choosing "Add a number" option
      this._value$.next(null);
      this._countryIsoCode$.next(this.localeConfig.tuiConfig.countryIsoCode);
      this.input = '';
      this.onChange(null);
      this._isInputMode$.next(true);
      return;
    }

    this.markAsTouched();
    this._value$.next(value);

    if (value?.phoneNumber) {
      const detectedIsoCode = maskitoGetCountryFromNumber(value.phoneNumber, metadata);
      this._countryIsoCode$.next(detectedIsoCode ?? this.countryIsoCode);
    }

    this.onChange(value);
  }

  writeValue(value: PatientContact | null): void {
    this.onValueChange(value);
  }

  registerOnChange(fn: OnChangeFn): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  markAsTouched(): void {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  onInput(input: string): void {
    this.input = input;
    this.onValueChange({
      id: null,
      patientId: this.patientId,
      phoneNumber: input ?? '',
    });
  }

  backToList(): void {
    this._isInputMode$.next(false);
    this.selectFirstItem().subscribe();
  }
}
